import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import Modal from '../components/Modal';
import SchoolCard, { CustomListInfo } from '../components/SchoolCard';
import SchoolProfileModal from '../components/SchoolProfileModal';
import CoachReportModal from '../components/CoachReportModal';
import BulkMessagingModal from '../components/BulkMessagingModal';
import { Heart, Send, Plus, Edit2, Trash2, X, Tag } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import { schoolListsAPI, SchoolCategory, SchoolInList } from '../services/schoolListsAPI';
import { customListsAPI, CustomList, CustomListSchool } from '../services/customListsAPI';
import { familyAPI } from '../services/api';
import ProfileCompletenessPill from '../components/ProfileCompletenessPill';
import ErrorState from '../components/ErrorState';
import SchoolCardSkeleton from '../components/SchoolCardSkeleton';
import { EmptyState, Button, TextInput } from '../components/ui';
import { AthleteSnapshot } from '../hooks/useProfileCompleteness';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import { useToast } from '../contexts/ToastContext';
import { swrGet, swrSet, swrInvalidate } from '../utils/swrCache';
import { isBestFit } from '../constants/thresholds';

type TabType = 'dream' | 'target' | 'safety' | `custom-${number}`;

interface ExtendedSchoolInList extends SchoolInList {
  coaching_staff?: any[];
}

// Shimmering placeholder shown in place of a numeric count while the lists load,
// so the header doesn't flash a misleading "0" before real values arrive.
const CountSkeleton: React.FC<{ width?: number; height?: number; onDark?: boolean }> = ({
  width = 44, height = 30, onDark = false,
}) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block',
      width,
      height,
      borderRadius: 8,
      background: onDark
        ? 'linear-gradient(90deg, rgba(255,255,255,0.25) 25%, rgba(255,255,255,0.45) 37%, rgba(255,255,255,0.25) 63%)'
        : 'linear-gradient(90deg, #eef1f5 25%, #e3e8ef 37%, #eef1f5 63%)',
      backgroundSize: '400% 100%',
      animation: 'aps-count-shimmer 1.4s ease-in-out infinite',
      verticalAlign: 'middle',
    }}
  />
);

const CollegeLists: React.FC = () => {
  const { sessionToken } = useAuth();
  const { requirePaid } = useSubscriptionGuard();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsive();
  const toast = useToast();
  const [athleteId, setAthleteId] = useState<string>('');
  const [athleteSnapshot, setAthleteSnapshot] = useState<AthleteSnapshot | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('dream');
  const [isLoading, setIsLoading] = useState(true);
  // Distinguishes a real failure from a genuinely-empty list so the page can
  // offer Retry instead of dead-ending on "Loading schools…" forever (#166).
  const [loadError, setLoadError] = useState(false);
  // Monotonic token so switching custom-list tabs quickly can't paint the
  // previous list's schools when its response lands late (#177).
  const customListReqRef = useRef(0);
  const [isPillLoading, setIsPillLoading] = useState(false);
  const [bestFitsCount, setBestFitsCount] = useState(0);
  const [openRostersCount, setOpenRostersCount] = useState(0);
  
  const [schoolLists, setSchoolLists] = useState<{
    dreamSchools: ExtendedSchoolInList[];
    targetSchools: ExtendedSchoolInList[];
    safetySchools: ExtendedSchoolInList[];
    totals: {
      dreamCount: number;
      targetCount: number;
      safetyCount: number;
      totalCount: number;
    };
  }>({
    dreamSchools: [],
    targetSchools: [],
    safetySchools: [],
    totals: { dreamCount: 0, targetCount: 0, safetyCount: 0, totalCount: 0 }
  });

  const [schoolInteractions, setSchoolInteractions] = useState<{
    [key: number]: {
      heart: boolean;
      star: boolean;
      bookmark: boolean;
    }
  }>({});

  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  // APS-260: the school whose coach change report is open.
  const [reportSchoolId, setReportSchoolId] = useState<number | null>(null);
  const [isBulkMessagingModalVisible, setIsBulkMessagingModalVisible] = useState(false);
  const [pillLoadingStates, setPillLoadingStates] = useState<{ [key: string]: boolean | string }>({});

  // Custom list state
  const [customLists, setCustomLists] = useState<CustomList[]>([]);
  const [customListSchools, setCustomListSchools] = useState<CustomListSchool[]>([]);
  const [schoolMemberships, setSchoolMemberships] = useState<{ [schoolId: number]: number[] }>({});
  const [isCustomListLoading, setIsCustomListLoading] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListColor, setNewListColor] = useState('#3B82F6');
  const [confirmDeleteListId, setConfirmDeleteListId] = useState<number | null>(null);

  // Scroll to top instantly when tab changes
  useEffect(() => {
    // Find the AppLayout's scrollable content div and jump it to top instantly
    const scrollableContent = document.querySelector('[data-app-scroll]');
    if (scrollableContent) {
      scrollableContent.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [activeTab]);

  // Load athlete ID on mount
  const initializeData = useCallback(async () => {
    if (!sessionToken) return;

    setLoadError(false);
    try {
      // Stale-while-revalidate: the same 'myAthletes:first' cache Dashboard
      // uses, so hopping between pages resolves the athlete with no network
      // wait. The fetch below still runs and refreshes the snapshot.
      const cachedAthlete = swrGet<AthleteSnapshot & { id: string }>('myAthletes:first', 10 * 60 * 1000);
      if (cachedAthlete?.id) {
        setAthleteId(cachedAthlete.id);
        setAthleteSnapshot(cachedAthlete);
      }

      // Freemium-redesign Phase 1: canonical my-athletes endpoint handles
      // guardian users (linked via guardian_athlete_relationships, not
      // users.athlete_profile_id) which is what was leaving this page
      // stuck on "Loading schools…" forever for parents.
      const myAthletes = await familyAPI.getMyAthletes(sessionToken);
      const first = myAthletes.athletes?.[0];
      if (first?.id) {
        swrSet('myAthletes:first', first);
        setAthleteId(first.id);
        setAthleteSnapshot(first); // P2.5 — drives the team-gap banner
      } else if (!cachedAthlete?.id) {
        // No athlete resolved and nothing cached — stop the infinite skeleton
        // and let the empty-state render (#166).
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error loading athlete:', error);
      // Only a hard dead-end if we have nothing to show yet.
      if (!athleteId) {
        setLoadError(true);
        setIsLoading(false);
      }
    }
  }, [sessionToken, athleteId]);

  useEffect(() => {
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  // Load school lists when athlete ID is available
  useEffect(() => {
    if (athleteId) {
      loadSchoolLists();
      loadCustomLists();
    }
  }, [athleteId, sessionToken]);

  // Load custom lists and memberships
  const loadCustomLists = async () => {
    if (!sessionToken || !athleteId) return;
    try {
      const [listsRes, membershipsRes] = await Promise.all([
        customListsAPI.getCustomLists(sessionToken, athleteId),
        customListsAPI.getSchoolMemberships(sessionToken, athleteId)
      ]);
      if (listsRes.success) setCustomLists(listsRes.data.lists);
      if (membershipsRes.success) setSchoolMemberships(membershipsRes.data.memberships);
    } catch (error) {
      console.error('Error loading custom lists:', error);
    }
  };

  // Load schools for a specific custom list tab
  const loadCustomListSchools = async (listId: number) => {
    if (!sessionToken || !athleteId) return;
    // Clear immediately + token this request so a stale response for a tab the
    // user already switched away from can't paint the wrong list (#177).
    const reqId = ++customListReqRef.current;
    setCustomListSchools([]);
    setIsCustomListLoading(true);
    try {
      const response = await customListsAPI.getListSchools(sessionToken, athleteId, listId);
      if (reqId !== customListReqRef.current) return;
      if (response.success) {
        setCustomListSchools(response.data.schools as any);
      }
    } catch (error) {
      if (reqId !== customListReqRef.current) return;
      console.error('Error loading custom list schools:', error);
      toast.error('Could not load this list. Please try again.');
    } finally {
      if (reqId === customListReqRef.current) setIsCustomListLoading(false);
    }
  };

  // Apply a school-lists payload to state (shared by the fetch below and the
  // stale-while-revalidate cache path).
  const applySchoolLists = (data: typeof schoolLists) => {
    setSchoolLists(data);

    // Convert to interaction state format
    const interactions: { [key: number]: { heart: boolean; star: boolean; bookmark: boolean } } = {};

    // Process all schools from all categories
    const allSchools = [
      ...data.dreamSchools.map(s => ({ ...s, category: 'dream' as SchoolCategory })),
      ...data.targetSchools.map(s => ({ ...s, category: 'target' as SchoolCategory })),
      ...data.safetySchools.map(s => ({ ...s, category: 'safety' as SchoolCategory }))
    ];

    allSchools.forEach(school => {
      interactions[school.school_id] = {
        heart: school.category === 'dream',
        star: school.category === 'target',
        bookmark: school.category === 'safety'
      };
    });

    setSchoolInteractions(interactions);

    // Calculate best fits using the shared threshold so portal + mobile agree (#125).
    const bestFits = allSchools.filter(s => isBestFit((s as any).match_score_norm)).length;
    setBestFitsCount(bestFits);

    // Calculate open rosters
    const openRosters = allSchools.filter(s => s.roster_status === 'Open Roster').length;
    setOpenRostersCount(openRosters);
  };

  const loadSchoolLists = async (isPillUpdate: boolean = false) => {
    if (!sessionToken || !athleteId) return;

    // Stale-while-revalidate: paint the last lists (counts + grids) instantly on
    // return; the fetch below still runs and replaces them with fresh data.
    const listKey = `collegeLists:${athleteId}`;
    const cached = isPillUpdate ? null : swrGet<typeof schoolLists>(listKey, 10 * 60 * 1000);
    if (cached) applySchoolLists(cached);

    try {
      if (!isPillUpdate && !cached) {
        setIsLoading(true);
      }

      const response = await schoolListsAPI.getSchoolLists(sessionToken, athleteId);

      if (response.success) {
        applySchoolLists(response.data);
        swrSet(listKey, response.data);
        setLoadError(false);
      }
    } catch (error) {
      console.error('Error loading school lists:', error);
      // Only a dead-end if there was nothing cached to show (#166/#172).
      if (!isPillUpdate && !cached) setLoadError(true);
    } finally {
      if (!isPillUpdate) {
        setIsLoading(false);
      }
    }
  };

  const handlePillPress = async (schoolId: number, pillType: 'dream' | 'target' | 'safety') => {
    if (!sessionToken || !athleteId) return;
    
    const pillKey = `${schoolId}-${pillType}`;
    const pillTypeMap: { [key: string]: 'heart' | 'star' | 'bookmark' } = {
      dream: 'heart',
      target: 'star',
      safety: 'bookmark'
    };
    const actualPillType = pillTypeMap[pillType];
    
    setPillLoadingStates(prev => ({ ...prev, [pillKey]: true, [`${schoolId}-targetType`]: actualPillType }));
    setIsPillLoading(true);
    
    const currentState = schoolInteractions[schoolId]?.[actualPillType];
    
    try {
      if (currentState) {
        await schoolListsAPI.removeSchool(sessionToken, athleteId, schoolId);
      } else {
        await schoolListsAPI.addSchool(sessionToken, athleteId, schoolId, pillType);
      }
      
      // Update local state
      setSchoolInteractions(current => {
        const newState = { ...current };
        
        if (currentState) {
          newState[schoolId] = {
            heart: false,
            star: false,
            bookmark: false
          };
        } else {
          newState[schoolId] = {
            heart: actualPillType === 'heart',
            star: actualPillType === 'star',
            bookmark: actualPillType === 'bookmark'
          };
        }
        
        return newState;
      });
      
      // Reload school lists to get updated counts
      await loadSchoolLists(true);
      swrInvalidate((k) => k.startsWith('collegeLists:') || k.startsWith('dashSchools:'));

    } catch (error) {
      console.error('Error updating school list:', error);
      toast.error('Could not update your list. Please try again.');
    } finally {
      setPillLoadingStates(prev => {
        const newState = { ...prev };
        delete newState[pillKey];
        delete newState[`${schoolId}-targetType`];
        return newState;
      });
      setIsPillLoading(false);
    }
  };

  // Custom list handlers
  const handleToggleCustomList = async (schoolId: number, listId: number, isInList: boolean) => {
    if (!sessionToken || !athleteId) return;
    try {
      if (isInList) {
        await customListsAPI.removeSchoolFromList(sessionToken, athleteId, listId, schoolId);
      } else {
        await customListsAPI.addSchoolToList(sessionToken, athleteId, listId, schoolId);
      }
      // Refresh memberships and lists
      await loadCustomLists();
      // If viewing this custom list tab, refresh its schools
      if (activeTab === `custom-${listId}`) {
        await loadCustomListSchools(listId);
      }
    } catch (error) {
      console.error('Error toggling custom list:', error);
      toast.error('Could not update the list. Please try again.');
    }
  };

  const handleCreateCustomList = async (schoolId: number | null, listName: string, color: string) => {
    if (!sessionToken || !athleteId) return;
    try {
      const createRes = await customListsAPI.createList(sessionToken, athleteId, listName, color);
      if (createRes.success && schoolId) {
        await customListsAPI.addSchoolToList(sessionToken, athleteId, createRes.data.id, schoolId);
      }
      await loadCustomLists();
    } catch (error) {
      console.error('Error creating custom list:', error);
      toast.error('Could not create the list. Please try again.');
    }
  };

  const handleDeleteCustomList = async (listId: number) => {
    if (!sessionToken || !athleteId) return;
    try {
      await customListsAPI.deleteList(sessionToken, athleteId, listId);
      if (activeTab === `custom-${listId}`) setActiveTab('dream');
      await loadCustomLists();
      setConfirmDeleteListId(null);
    } catch (error) {
      console.error('Error deleting custom list:', error);
      toast.error('Could not delete the list. Please try again.');
    }
  };

  const handleSchoolCardPress = (schoolId: number) => {
    setSelectedSchoolId(schoolId);
    setIsProfileModalVisible(true);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalVisible(false);
    setSelectedSchoolId(null);
  };

  const getCurrentSchools = () => {
    if (typeof activeTab === 'string' && activeTab.startsWith('custom-')) {
      return customListSchools;
    }
    switch (activeTab) {
      case 'dream':
        return schoolLists.dreamSchools;
      case 'target':
        return schoolLists.targetSchools;
      case 'safety':
        return schoolLists.safetySchools;
      default:
        return [];
    }
  };

  // Load custom list schools when switching to a custom tab
  useEffect(() => {
    if (typeof activeTab === 'string' && activeTab.startsWith('custom-')) {
      const listId = parseInt(activeTab.split('-')[1]);
      loadCustomListSchools(listId);
    }
  }, [activeTab]);

  const isCustomTab = typeof activeTab === 'string' && activeTab.startsWith('custom-');
  const activeCustomList = isCustomTab ? customLists.find(l => `custom-${l.id}` === activeTab) : null;

  const currentSchools = getCurrentSchools();

  return (
    <AppLayout>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes aps-count-shimmer {
          0% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      
      <div style={{ padding: isMobile ? '16px' : '24px', maxWidth: '1600px', margin: '0 auto', fontFamily: 'Manrope, sans-serif' }}>
        {/* One completeness surface, one component across the app — the same
            persistent pill Find Colleges + mobile use (was a separate louder
            ProfileGapBanner here, which read as a different kind of nudge). */}
        {athleteSnapshot && (
          <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
            <ProfileCompletenessPill athlete={athleteSnapshot} />
          </div>
        )}
        {/* Header with Stats - Contained Card */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          marginBottom: isMobile ? '16px' : '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #E5E7EB',
          padding: isMobile ? '16px' : '24px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '16px' : '0'
          }}>
            {/* Stats - Matching Mobile */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: isMobile ? '16px' : '48px',
              width: isMobile ? '100%' : 'auto'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '700', color: '#151133', marginBottom: '4px' }}>
                  {isLoading ? <CountSkeleton width={isMobile ? 36 : 48} height={isMobile ? 24 : 32} /> : schoolLists.totals.totalCount}
                </div>
                <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#6B7280', fontWeight: '500' }}>
                  Total Schools
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '700', color: '#10B981', marginBottom: '4px' }}>
                  {isLoading ? <CountSkeleton width={isMobile ? 36 : 48} height={isMobile ? 24 : 32} /> : openRostersCount}
                </div>
                <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#6B7280', fontWeight: '500' }}>
                  Open in Lists
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '700', color: '#10B981', marginBottom: '4px' }}>
                  {isLoading ? <CountSkeleton width={isMobile ? 36 : 48} height={isMobile ? 24 : 32} /> : bestFitsCount}
                </div>
                <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#6B7280', fontWeight: '500' }}>
                  Best Fits
                </div>
              </div>
            </div>
            
            <Button
              variant="primary"
              size="md"
              fullWidth={isMobile}
              leftIcon={<Send size={16} aria-hidden />}
              onClick={() => setIsBulkMessagingModalVisible(true)}
            >
              Send a Message
            </Button>
          </div>
        </div>

        {/* Tabs — the 3 heart interest lists (primary) then the user's custom folders. */}
        <div role="tablist" aria-label="Interest lists and custom folders" style={{
          background: 'white',
          borderRadius: '12px',
          padding: isMobile ? '4px' : '8px',
          marginBottom: isMobile ? '16px' : '24px',
          display: 'flex',
          gap: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          border: '1px solid #E5E7EB',
          overflowX: 'auto' as const,
          WebkitOverflowScrolling: 'touch' as any,
          scrollbarWidth: 'none' as const,
          msOverflowStyle: 'none' as any
        }}>
          <button
            role="tab"
            aria-selected={activeTab === 'dream'}
            aria-label="3-heart interest list"
            onClick={() => setActiveTab('dream')}
            style={{
              flex: '0 0 auto',
              minWidth: isMobile ? '80px' : '100px',
              padding: '20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'dream' ? '#10B981' : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column' as const,
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'dream') e.currentTarget.style.background = '#F9FAFB';
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'dream') e.currentTarget.style.background = 'transparent';
            }}
          >
            <div style={{ display: 'flex', gap: '4px' }}>
              <Heart size={20} fill={activeTab === 'dream' ? 'white' : '#10B981'} color={activeTab === 'dream' ? 'white' : '#10B981'} />
              <Heart size={20} fill={activeTab === 'dream' ? 'white' : '#10B981'} color={activeTab === 'dream' ? 'white' : '#10B981'} />
              <Heart size={20} fill={activeTab === 'dream' ? 'white' : '#10B981'} color={activeTab === 'dream' ? 'white' : '#10B981'} />
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: activeTab === 'dream' ? 'white' : '#374151' }}>
              {isLoading ? <CountSkeleton width={26} height={22} onDark={activeTab === 'dream'} /> : schoolLists.totals.dreamCount}
            </div>
          </button>

          <button
            role="tab"
            aria-selected={activeTab === 'target'}
            aria-label="2-heart interest list"
            onClick={() => setActiveTab('target')}
            style={{
              flex: '0 0 auto',
              minWidth: isMobile ? '80px' : '100px',
              padding: '20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'target' ? '#F59E0B' : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column' as const,
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'target') e.currentTarget.style.background = '#F9FAFB';
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'target') e.currentTarget.style.background = 'transparent';
            }}
          >
            <div style={{ display: 'flex', gap: '4px' }}>
              <Heart size={20} fill={activeTab === 'target' ? 'white' : '#F59E0B'} color={activeTab === 'target' ? 'white' : '#F59E0B'} />
              <Heart size={20} fill={activeTab === 'target' ? 'white' : '#F59E0B'} color={activeTab === 'target' ? 'white' : '#F59E0B'} />
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: activeTab === 'target' ? 'white' : '#374151' }}>
              {isLoading ? <CountSkeleton width={26} height={22} onDark={activeTab === 'target'} /> : schoolLists.totals.targetCount}
            </div>
          </button>

          <button
            role="tab"
            aria-selected={activeTab === 'safety'}
            aria-label="1-heart interest list"
            onClick={() => setActiveTab('safety')}
            style={{
              flex: '0 0 auto',
              minWidth: isMobile ? '80px' : '100px',
              padding: '20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'safety' ? '#DC2626' : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column' as const,
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'safety') e.currentTarget.style.background = '#F9FAFB';
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'safety') e.currentTarget.style.background = 'transparent';
            }}
          >
            <div style={{ display: 'flex', gap: '4px' }}>
              <Heart size={20} fill={activeTab === 'safety' ? 'white' : '#DC2626'} color={activeTab === 'safety' ? 'white' : '#DC2626'} />
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: activeTab === 'safety' ? 'white' : '#374151' }}>
              {isLoading ? <CountSkeleton width={26} height={22} onDark={activeTab === 'safety'} /> : schoolLists.totals.safetyCount}
            </div>
          </button>

          {/* One lists system, two groups: the 3 hearts above are your pinned
              INTEREST lists (desire ranking); these are your own folders. Same
              membership model — a school can sit in an interest list and folders
              at once, and deleting a folder never touches the others. */}
          {customLists.length > 0 && (
            <div aria-hidden style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 2px' }}>
              <div style={{ width: '1px', height: '32px', background: '#E5E7EB' }} />
              <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', color: '#9CA3AF', textTransform: 'uppercase' }}>Folders</span>
            </div>
          )}
          {/* Custom List Tabs */}
          {customLists.map(list => {
            const isActive = activeTab === `custom-${list.id}`;
            return (
              <button
                key={list.id}
                role="tab"
                aria-selected={isActive}
                aria-label={`${list.name} list`}
                onClick={() => setActiveTab(`custom-${list.id}` as TabType)}
                style={{
                  flex: '0 0 auto',
                  minWidth: isMobile ? '80px' : '100px',
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  background: isActive ? list.color : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column' as const,
                  alignItems: 'center',
                  gap: '4px',
                  position: 'relative' as const
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = '#F9FAFB';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'transparent';
                }}
              >
                <Tag size={18} color={isActive ? 'white' : list.color} fill={isActive ? 'rgba(255,255,255,0.3)' : `${list.color}33`} />
                <div style={{
                  fontSize: '12px', fontWeight: '600',
                  color: isActive ? 'white' : '#6B7280',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  maxWidth: '100%'
                }}>
                  {list.name}
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: isActive ? 'white' : '#374151' }}>
                  {list.school_count}
                </div>
                {isActive && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteListId(list.id); }}
                    aria-label={`Delete ${list.name} list`}
                    style={{
                      position: 'absolute', top: '4px', right: '4px',
                      width: '18px', height: '18px', borderRadius: '50%',
                      border: 'none', background: 'rgba(255,255,255,0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', padding: 0
                    }}
                  >
                    <X size={10} color="white" strokeWidth={3} />
                  </button>
                )}
              </button>
            );
          })}

          {/* Add List Tab */}
          <button
            onClick={() => setShowCreateListModal(true)}
            style={{
              width: '48px', minWidth: '48px',
              padding: '12px 0',
              border: '2px dashed #D1D5DB',
              borderRadius: '8px',
              background: 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#3B82F6';
              e.currentTarget.style.background = '#EFF6FF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#D1D5DB';
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <Plus size={20} color="#6B7280" />
          </button>
        </div>

        {/* Create List Inline Modal */}
        {showCreateListModal && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: isMobile ? '16px' : '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #3B82F6',
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label htmlFor="new-list-name" style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>List Name</label>
              {/* The compact padding and 14px type are kept: this sits in a
                  one-line composer beside the colour swatches and the two
                  buttons, all aligned flex-end, and the primitive's roomier
                  default would push that row out of line. */}
              <TextInput
                id="new-list-name"
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newListName.trim()) {
                    handleCreateCustomList(null, newListName.trim(), newListColor);
                    setNewListName('');
                    setShowCreateListModal(false);
                  }
                  if (e.key === 'Escape') {
                    setShowCreateListModal(false);
                    setNewListName('');
                  }
                }}
                placeholder="e.g. Texas Schools, In-State, Visited..."
                autoFocus
                style={{ padding: '8px 12px', fontSize: '14px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Color</label>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {['#EF4444', '#F97316', '#F59E0B', '#10B981', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899', '#6B7280'].map(c => (
                  <button
                    key={c}
                    onClick={() => setNewListColor(c)}
                    style={{
                      width: '24px', height: '24px', borderRadius: '6px',
                      background: c, border: newListColor === c ? '2px solid #151133' : '2px solid transparent',
                      cursor: 'pointer', padding: 0
                    }}
                  />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => {
                  if (newListName.trim()) {
                    handleCreateCustomList(null, newListName.trim(), newListColor);
                    setNewListName('');
                    setShowCreateListModal(false);
                  }
                }}
                disabled={!newListName.trim()}
                style={{
                  padding: '8px 16px', border: 'none', borderRadius: '8px',
                  background: newListName.trim() ? '#3B82F6' : '#E5E7EB',
                  color: newListName.trim() ? 'white' : '#9CA3AF',
                  fontSize: '14px', fontWeight: '600', cursor: newListName.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Create
              </button>
              <button
                onClick={() => { setShowCreateListModal(false); setNewListName(''); }}
                style={{
                  padding: '8px 16px', border: '1px solid #E5E7EB', borderRadius: '8px',
                  background: 'white', color: '#6B7280', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* School Cards */}
        {loadError ? (
          <ErrorState
            message="We couldn't load your school lists. Check your connection and try again."
            onRetry={() => { setIsLoading(true); setLoadError(false); athleteId ? loadSchoolLists() : initializeData(); }}
          />
        ) : (isLoading || isCustomListLoading) ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: isMobile ? '16px' : '20px',
            }}
            role="status"
            aria-label="Loading schools"
            aria-busy="true"
          >
            {Array.from({ length: 6 }).map((_, i) => <SchoolCardSkeleton key={i} />)}
          </div>
        ) : currentSchools.length === 0 ? (
          <EmptyState
            icon={<Heart size={28} />}
            title="No schools in this list yet"
            subtitle="Start adding schools from the matches grid to build your list."
            action={<Button onClick={() => navigate('/dashboard')}>Browse schools</Button>}
          />
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: isMobile ? '16px' : '20px'
          }}>
            {currentSchools.map(school => (
              <SchoolCard
                key={school.school_id}
                school={school}
                onCardClick={handleSchoolCardPress}
                onAddToList={handlePillPress}
                schoolInteractions={schoolInteractions[school.school_id] || { heart: false, star: false, bookmark: false }}
                isLoading={Object.keys(pillLoadingStates).some(key => key.startsWith(`${school.school_id}-`))}
                customLists={customLists.map(l => ({ id: l.id, name: l.name, color: l.color, school_count: l.school_count }))}
                schoolCustomListIds={schoolMemberships[school.school_id] || []}
                onToggleCustomList={handleToggleCustomList}
                onCreateCustomList={(schoolId, name, color) => handleCreateCustomList(schoolId, name, color)}
                onReportCoach={setReportSchoolId}
              />
            ))}
          </div>
        )}
      </div>

      {/* School Profile Modal */}
      <SchoolProfileModal
        isOpen={isProfileModalVisible}
        onClose={handleCloseProfileModal}
        schoolId={selectedSchoolId}
        athleteProfileId={athleteId}
      />

      {reportSchoolId != null && (
        <CoachReportModal
          isOpen
          onClose={() => setReportSchoolId(null)}
          schoolId={reportSchoolId}
          schoolName={(currentSchools as Array<{ school_id: number; name?: string | null }>).find(s => s.school_id === reportSchoolId)?.name}
          athleteId={athleteId || undefined}
          loadCoaches
        />
      )}

      {/* Bulk Messaging Modal */}
      <BulkMessagingModal
        isOpen={isBulkMessagingModalVisible}
        onClose={() => setIsBulkMessagingModalVisible(false)}
        schools={[...schoolLists.dreamSchools, ...schoolLists.targetSchools, ...schoolLists.safetySchools]}
        athleteId={athleteId}
        athleteName="Athlete"
        customLists={customLists}
        schoolMemberships={schoolMemberships}
        sessionToken={sessionToken || ''}
        onProgramsUnlocked={() => {
          swrInvalidate((k) => k.startsWith('collegeLists:') || k.startsWith('dashSchools:'));
          loadSchoolLists(true);
          if (typeof activeTab === 'string' && activeTab.startsWith('custom-')) {
            loadCustomListSchools(Number(activeTab.slice('custom-'.length)));
          }
        }}
      />

      {/* Delete Confirm Modal */}
      {confirmDeleteListId !== null && (() => {
        const listToDelete = customLists.find(l => l.id === confirmDeleteListId);
        return (
          <Modal
            isOpen={true}
            onClose={() => setConfirmDeleteListId(null)}
            ariaLabel="Delete list"
            contentStyle={{ maxWidth: '400px', padding: '32px', textAlign: 'center', borderRadius: '16px' }}
          >
            <div>
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: '#FEE2E2', margin: '0 auto 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Trash2 size={24} color="#EF4444" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#151133', fontFamily: 'Manrope, sans-serif' }}>
                Delete List
              </h3>
              <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#6B7280', lineHeight: '1.5', fontFamily: 'Manrope, sans-serif' }}>
                Delete <strong>"{listToDelete?.name}"</strong>? The schools in it stay saved and remain in any of your other lists — only this list is removed.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={() => setConfirmDeleteListId(null)}
                  style={{
                    padding: '10px 24px', border: '1px solid #E5E7EB', borderRadius: '10px',
                    background: 'white', color: '#374151', fontSize: '14px', fontWeight: '600',
                    cursor: 'pointer', fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteCustomList(confirmDeleteListId)}
                  style={{
                    padding: '10px 24px', border: 'none', borderRadius: '10px',
                    background: '#EF4444', color: 'white', fontSize: '14px', fontWeight: '600',
                    cursor: 'pointer', fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </Modal>
        );
      })()}
    </AppLayout>
  );
};

export default CollegeLists;
